import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { sanitizeLeaguePreferenceState } from '../utils/leaguePreference';
import { clashWinnerIconToApi, sanitizeClashWinnerIcon } from '../utils/clashWinnerIcon';
import { clearClientSessionData } from '../utils/clearClientSessionData';

/** Keep in sync with ProtectedRoute POST_LOGIN_REDIRECT_KEY */
const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to decode JWT token (without verification, just to get user info)
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

const buildUserFromToken = (token, previousUser = null) => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.email || !decoded.username) return null;

  const preference = sanitizeLeaguePreferenceState(decoded.lp, decoded.ld);

  return {
    username: decoded.username,
    email: decoded.email,
    country: decoded.country,
    coinBalance: decoded.coinBalance ?? previousUser?.coinBalance ?? 0,
    inventory:
      decoded.inventory != null && typeof decoded.inventory === 'object' && !Array.isArray(decoded.inventory)
        ? decoded.inventory
        : {},
    tieredLeagues: decoded.tieredLeagues || {},
    lp: preference.leaguePreference,
    ld: preference.leagueDenied,
    w: sanitizeClashWinnerIcon(decoded.w),
    clashWinnerIcon: clashWinnerIconToApi(decoded.w),
    pendingInviteCount:
      decoded.pendingInviteCount != null
        ? Number(decoded.pendingInviteCount) || 0
        : previousUser?.pendingInviteCount ?? 0
  };
};

/** Merge GET /me into auth user (source of truth for coin balance). */
const mergeMeIntoUser = (me, prev) => {
  if (!prev) return prev;
  const preference = sanitizeLeaguePreferenceState(
    me.leaguePreference ?? prev.lp,
    me.leagueDenied ?? prev.ld
  );
  return {
    ...prev,
    coinBalance: me.coinBalance ?? prev.coinBalance,
    inventory:
      me.inventory != null && typeof me.inventory === 'object' && !Array.isArray(me.inventory)
        ? me.inventory
        : prev.inventory,
    tieredLeagues: me.tieredLeagues != null ? me.tieredLeagues : prev.tieredLeagues,
    lp: preference.leaguePreference,
    ld: preference.leagueDenied,
    clashWinnerIcon: me.clashWinnerIcon != null
      ? clashWinnerIconToApi(me.clashWinnerIcon)
      : prev.clashWinnerIcon,
    w: me.clashWinnerIcon != null
      ? sanitizeClashWinnerIcon(me.clashWinnerIcon)
      : prev.w,
    pendingInviteCount:
      me.pendingInviteCount != null ? Number(me.pendingInviteCount) || 0 : prev.pendingInviteCount ?? 0
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const applyLoggedOut = useCallback(() => {
    clearClientSessionData();
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const applyNewToken = useCallback(async (newToken) => {
    clearClientSessionData({ preserveKeys: ['token'] });
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    const decodedUser = buildUserFromToken(newToken);
    if (!decodedUser) {
      applyLoggedOut();
      return;
    }
    setToken(newToken);
    setUser(decodedUser);
    try {
      const { data } = await api.get('/me');
      setUser((prev) => mergeMeIntoUser(data, prev));
    } catch {
      /* keep JWT-derived user if /me fails */
    }
  }, [applyLoggedOut]);

  /** Reconcile React auth with localStorage (other-tab login/logout, missed storage events). */
  const syncAuthFromStorage = useCallback(() => {
    let stored = null;
    try {
      stored = localStorage.getItem('token');
    } catch {
      stored = null;
    }
    if (stored === tokenRef.current) return 'same';
    if (!stored) {
      applyLoggedOut();
      return 'logged-out';
    }
    void applyNewToken(stored);
    return 'switched';
  }, [applyLoggedOut, applyNewToken]);

  useEffect(() => {
    const restoreSession = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
        const decodedUser = buildUserFromToken(storedToken);
        if (decodedUser) {
          setUser(decodedUser);
          setToken(storedToken);
          try {
            const { data } = await api.get('/me');
            setUser((prev) => mergeMeIntoUser(data, prev));
          } catch {
            /* keep JWT-derived user if /me fails (offline, etc.) */
          }
        } else {
          clearClientSessionData();
          setToken(null);
          setUser(null);
          delete api.defaults.headers.common['Authorization'];
        }
      } else {
        setToken(null);
        setUser(null);
        delete api.defaults.headers.common['Authorization'];
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  // Update API header when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Handle session expired (401 from API) - wipe client data and log out
  useEffect(() => {
    const handleSessionExpired = () => {
      // api interceptor already wiped storage; keep sessionExpired flag for Login banner
      clearClientSessionData({ preserveKeys: ['sessionExpired'] });
      setToken(null);
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // Cross-tab auth: storage events (immediate) + visibility/focus (Safari / missed events).
  useEffect(() => {
    const handleStorage = (event) => {
      // Ignore sessionStorage; accept null storageArea (Safari sometimes omits it).
      if (event.storageArea === sessionStorage) return;
      if (event.key != null && event.key !== 'token') return;
      syncAuthFromStorage();
    };

    const handleVisible = () => {
      if (document.visibilityState === 'visible') {
        syncAuthFromStorage();
      }
    };

    window.addEventListener('storage', handleStorage);
    document.addEventListener('visibilitychange', handleVisible);
    window.addEventListener('focus', handleVisible);
    return () => {
      window.removeEventListener('storage', handleStorage);
      document.removeEventListener('visibilitychange', handleVisible);
      window.removeEventListener('focus', handleVisible);
    };
  }, [syncAuthFromStorage]);

  const login = async (email, password, turnstileToken) => {
    try {
      const response = await api.post('/auth/login', { email, password, turnstileToken });
      const { token: newToken, user: userData } = response.data;
      // Wipe prior player data on this origin; keep redirect target for Login page.
      clearClientSessionData({ preserveKeys: [POST_LOGIN_REDIRECT_KEY] });
      localStorage.setItem('token', newToken);
      setToken(newToken);
      const preference = sanitizeLeaguePreferenceState(
        userData.leaguePreference,
        userData.leagueDenied
      );
      setUser({
        ...userData,
        lp: preference.leaguePreference,
        ld: preference.leagueDenied,
        clashWinnerIcon: clashWinnerIconToApi(userData.clashWinnerIcon ?? userData.w),
        w: sanitizeClashWinnerIcon(userData.clashWinnerIcon ?? userData.w),
        pendingInviteCount: Number(userData.pendingInviteCount) || 0
      });
      api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      try {
        const { data } = await api.get('/me');
        setUser((prev) => mergeMeIntoUser(data, prev));
      } catch {
        /* keep login payload if /me fails */
      }
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
        code: error.response?.data?.code
      };
    }
  };

  const register = async (username, email, password, country, dateOfBirth, leaguePreference, turnstileToken) => {
    try {
      const { data } = await api.post('/auth/register', {
        username,
        email,
        password,
        country,
        dateOfBirth,
        leaguePreference,
        turnstileToken
      });
      return { success: true, message: data?.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Registration failed',
        code: error.response?.data?.code
      };
    }
  };

  const resendVerification = async (emailAddress) => {
    try {
      const { data } = await api.post('/auth/resend-verification', { email: emailAddress });
      return { success: true, message: data?.message };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Could not resend email'
      };
    }
  };

  const logout = () => {
    clearClientSessionData();
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
  };

  const refreshUser = useCallback(async () => {
    // Never refresh a stale in-memory session after another tab logged out/in.
    if (syncAuthFromStorage() !== 'same') return;
    if (!tokenRef.current) return;
    const currentToken = tokenRef.current;
    try {
      const { data } = await api.get('/me');
      setUser((prev) => {
        const base = prev ?? buildUserFromToken(currentToken);
        return base ? mergeMeIntoUser(data, base) : prev;
      });
    } catch {
      setUser((prev) => {
        const decodedUser = buildUserFromToken(currentToken, prev);
        return decodedUser ?? prev;
      });
    }
  }, [syncAuthFromStorage]);

  const updateToken = (newToken, userData) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    if (userData) setUser((prev) => ({ ...prev, ...userData }));
  };

  return (
    <AuthContext.Provider value={{ user, login, register, resendVerification, logout, refreshUser, updateToken, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

